// motion.js — gemeinsame Bewegung der App (Spec docs/superpowers/specs/2026-10-21-motion-design.md).
// Web Animations direkt am Element statt View Transitions: unterbrechbar, Eingaben gehen sofort durch.
// Regeln: nur transform/translate/scale/opacity/clip-path; eine neue Animation am selben Element startet beim
// Zwischenstand der alten; reduzierte Bewegung = nur Überblenden (120 ms linear), ohne Deckkraft keine Animation.
import springs from '../tokens/springs.json' with { type: 'json' };

const ALLOWED = new Set(['transform', 'translate', 'scale', 'opacity', 'clipPath', 'offset', 'easing', 'composite']);
const VISUAL = ['transform', 'translate', 'scale', 'opacity', 'clipPath'];
const running = new WeakMap();

/** Dauer + Kurve einer Expressive-Feder ('spatial-fast' … 'effects-slow'). */
export function timing(name) {
  const s = springs[name];
  if (!s) throw new Error('unbekannte Feder: ' + name);
  return { duration: s.durationMs, easing: s.easing };
}

const isReduced = () => typeof document !== 'undefined' && (document.documentElement.dataset.motion === 'reduced'
  || (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches));

/**
 * Animation abspielen. o: { spring = 'spatial-default', delay = 0, fill = 'none', reduced, getStyle }
 * Gibt die Animation zurück (oder null, wenn bei reduzierter Bewegung nichts zu tun ist).
 */
export function play(el, keyframes, o = {}) {
  for (const f of keyframes) for (const k of Object.keys(f)) if (!ALLOWED.has(k)) throw new Error('nicht animierbar (nur transform/opacity/clip-path): ' + k);
  if (!el || typeof el.animate !== 'function') return null;
  const reduced = o.reduced ?? isReduced();
  const getStyle = o.getStyle || ((e) => getComputedStyle(e));
  let kf = keyframes.map((f) => ({ ...f }));
  // Unterbrechen: laufende Animation abbrechen und beim aktuellen Stand weitermachen
  const prev = running.get(el);
  if (prev && prev.playState !== 'finished' && typeof prev.cancel === 'function') {
    const now = getStyle(el), first = {};
    for (const k of VISUAL) if (k in kf[0] && now[k] != null && now[k] !== '') first[k] = now[k];
    prev.cancel();
    kf[0] = { ...kf[0], ...first };
  }
  let opts;
  if (reduced) {
    if (!kf.some((f) => 'opacity' in f)) { running.delete(el); return null; }
    kf = [{ opacity: kf[0].opacity ?? 1 }, { opacity: kf[kf.length - 1].opacity ?? 1 }];
    opts = { duration: 120, easing: 'linear', fill: o.fill || 'none', delay: 0 };
  } else {
    opts = { ...timing(o.spring || 'spatial-default'), delay: o.delay || 0, fill: o.fill || 'none' };
  }
  const a = el.animate(kf, opts);
  running.set(el, a);
  return a;
}

const px = (n) => Math.round(n) + 'px';
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
/** Ausschnitt (clip-path inset) des Ziels, der genau das Rechteck des Ursprungs zeigt. */
export function insetOf(from, to, radius = 0) {
  const W = to.width, H = to.height;
  const l = clamp(from.left - to.left, 0, W), t = clamp(from.top - to.top, 0, H);
  const r = clamp(to.left + W - (from.left + from.width), 0, W), b = clamp(to.top + H - (from.top + from.height), 0, H);
  return `inset(${px(t)} ${px(r)} ${px(b)} ${px(l)} round ${px(radius)})`;
}

/** Container-Transform: das Ziel wächst aus dem Rechteck des Ursprungs (Liste/Marker → Detail); reverse = zurück. */
export function containerReveal(from, to, { fromRadius = 0, toRadius = 0, reverse = false } = {}) {
  const a = insetOf(from, to, fromRadius), b = `inset(0px 0px 0px 0px round ${px(toRadius)})`;
  const box = reverse ? [{ clipPath: b }, { clipPath: a }] : [{ clipPath: a }, { clipPath: b }];
  // Inhalt blendet erst ein, wenn der Container gewachsen ist (Fade Through im Container)
  const content = reverse ? [{ opacity: 1 }, { opacity: 0, offset: 0.35 }, { opacity: 0 }] : [{ opacity: 0 }, { opacity: 0, offset: 0.3 }, { opacity: 1 }];
  return { box, content };
}

/** Fade Through (Navigationsziele ohne Beziehung): Eingang aus 92 %. */
export const fadeThrough = () => [{ opacity: 0, transform: 'scale(0.92)' }, { opacity: 1, transform: 'scale(1)' }];

/** Shared Axis: 'x' (Reiter, Richtung ±1) oder 'z' (Eltern → Kind = +1, zurück = −1). */
export function sharedAxis(axis, dir = 1) {
  if (axis === 'z') return [{ opacity: 0, transform: `scale(${dir > 0 ? 0.8 : 1.1})` }, { opacity: 1, transform: 'scale(1)' }];
  return [{ opacity: 0, transform: `translateX(${30 * Math.sign(dir || 1)}px)` }, { opacity: 1, transform: 'translateX(0px)' }];
}

/** Feder-Pop (mit spatial-fast überschwingt die Kurve selbst). */
export const pop = () => [{ transform: 'scale(0.6)' }, { transform: 'scale(1)' }];

/** Shape-Morph zwischen zwei Formen gleicher Punktzahl (Shape-Library --vc-shape-*). */
export const shapeMorph = (from, to) => [{ clipPath: from }, { clipPath: to }];

const depth = (p) => String(p || '').split('/').filter(Boolean).length;
/** Welches Muster ein Seitenwechsel bekommt; null = keine Bewegung. */
export function navMotion(prev, next) {
  if (!prev || !next) return null;
  if ((prev.nav || '') !== (next.nav || '')) return { kind: 'through' };
  if (prev.path === next.path) return null;
  const d = depth(next.path) - depth(prev.path);
  return d === 0 ? null : { kind: 'axis', axis: 'z', dir: d > 0 ? 1 : -1 };
}

/** Merken: das Symbol federt (spatial-fast); beim Merken läuft ein Ring nach außen (nur Deko, bei reduzierter Bewegung aus). */
export function heartBeat(icon, on, o = {}) {
  if (!icon) return null;
  const a = play(icon, on ? pop() : [{ transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { spring: 'spatial-fast', ...o });
  const reduced = o.reduced ?? isReduced();
  const host = icon.parentElement;
  if (on && !reduced && host && typeof document !== 'undefined') {
    const ring = document.createElement('span');
    ring.className = 'vc-burst';
    ring.setAttribute('aria-hidden', 'true');
    ring.style.left = (icon.offsetLeft + icon.offsetWidth / 2) + 'px';
    ring.style.top = (icon.offsetTop + icon.offsetHeight / 2) + 'px';
    host.appendChild(ring);
    const r = play(ring, [{ transform: 'translate(-50%, -50%) scale(0.4)', opacity: 0.6 }, { transform: 'translate(-50%, -50%) scale(2)', opacity: 0 }], { spring: 'effects-slow', reduced: false });
    const drop = () => ring.remove();
    if (r) r.finished.then(drop, drop); else drop();
  }
  return a;
}

/** Neu freigeschaltet: Abzeichen morpht vom Keks zur Sonne (Shape-Library), federt auf, gestaffelt (höchstens 8 × 90 ms). */
export function unlockReveal(icons, o = {}) {
  if (!icons.length || typeof document === 'undefined') return [];
  const root = getComputedStyle(document.documentElement);
  const from = root.getPropertyValue('--vc-shape-cookie4').trim(), to = root.getPropertyValue('--vc-shape-sunny').trim();
  if (!from || !to) return [];
  return icons.map((el, i) => play(el, [{ clipPath: from, transform: 'scale(0.4) rotate(-90deg)', opacity: 0 },
    { clipPath: to, transform: 'none', opacity: 1 }], { spring: 'spatial-slow', delay: Math.min(i, 8) * 90, fill: 'backwards', ...o }));
}

/** Feste Elemente in Seiten (schwebende Knöpfe usw.): ein transform am Vorfahren würde sie mitnehmen. */
export const FIXED_SEL = '.pk-fab, .tr-fab, .vc-fab, .vc-toolbar.is-floating, [data-motion-fixed]';
const MAX_PARTS = 12;

/** Teile eines Bereichs, die sich bewegen dürfen, ohne ein festes Element mitzunehmen. */
export function movable(root) {
  if (!root.querySelector(FIXED_SEL)) return [root];
  const out = [];
  for (const c of root.children) if (!c.matches(FIXED_SEL)) out.push(...movable(c));
  return out;
}

/** Muster auf einen ganzen Bereich: Deckkraft auf den Bereich, Bewegung nur auf Teile ohne feste Elemente. */
export function playScene(root, keyframes, o = {}) {
  if (!root) return [];
  const parts = movable(root);
  if (parts.length === 1 && parts[0] === root) return [play(root, keyframes, o)];
  const op = keyframes.map((f) => ('opacity' in f ? { opacity: f.opacity } : {}));
  const tf = keyframes.map(({ opacity, ...rest }) => rest);
  const out = [play(root, op, o)];
  if (parts.length <= MAX_PARTS) for (const p of parts) out.push(play(p, tf, o));
  return out;
}

// ── Runde 2 (v442): Fade, Wischen, Zahlenrolle, Auf-/Zuklappen, FLIP ──
/** M3 „Fade“ für Elemente, die an Ort und Stelle erscheinen; nutzt die scale-Eigenschaft (ein vorhandenes transform bleibt). */
export const fadeIn = (from = 0.8) => [{ opacity: 0, scale: String(from) }, { opacity: 1, scale: '1' }];

/** Wisch-Entscheidung: dx (px, Vorzeichen = Richtung), v (px/ms), w = Breite. */
export function swipeOutcome(dx, v, w) {
  const dir = dx < 0 ? -1 : 1;
  const fast = Math.abs(v) > 0.5 && Math.sign(v) === dir;
  return { dismiss: Math.abs(dx) > w / 3 || fast, dir };
}

/** Neuer Wert: kommt aus der Richtung der Änderung herein (1 = größer → von unten). */
export const rollIn = (dir = 1) => [{ opacity: 0, transform: `translateY(${dir < 0 ? '-' : ''}0.6em)` }, { opacity: 1, transform: 'translateY(0)' }];

/** Aufklappen per Ausschnitt von oben (reverse = zuklappen) — keine Höhen-Animation. */
export function expand(reverse = false) {
  const shut = { opacity: 0, clipPath: 'inset(0px 0px 100% 0px)' }, open = { opacity: 1, clipPath: 'inset(0px 0px 0% 0px)' };
  return reverse ? [open, shut] : [shut, open];
}

/** FLIP: ein Element ist um dy verschoben worden → von der alten Lage aus hineinfedern. */
export const flip = (dy) => (Math.round(dy) ? [{ transform: `translateY(${Math.round(dy)}px)` }, { transform: 'translateY(0px)' }] : null);

/**
 * Wegwischen per Zeiger (waagerecht): das Element folgt dem Finger; losgelassen fliegt es in Wischrichtung hinaus
 * (onDismiss) oder federt zurück. Senkrecht bleibt Scrollen. Gibt eine Aufräumfunktion zurück.
 */
export function swipeToDismiss(el, onDismiss) {
  let start = null, last = null, dx = 0, dragging = false;
  const down = (e) => {
    if (e.button !== 0 || e.target.closest('button, a')) return;
    start = { x: e.clientX, y: e.clientY, t: e.timeStamp }; last = start; dx = 0; dragging = false;
  };
  const move = (e) => {
    if (!start) return;
    const ddx = e.clientX - start.x, ddy = e.clientY - start.y;
    if (!dragging) { if (Math.abs(ddx) < 8) return; if (Math.abs(ddy) > Math.abs(ddx)) { start = null; return; } dragging = true; el.setPointerCapture?.(e.pointerId); }
    dx = ddx; last = { x: e.clientX, t: e.timeStamp, prev: last };
    el.style.translate = dx + 'px 0'; el.style.opacity = String(Math.max(0.2, 1 - Math.abs(dx) / el.offsetWidth));
  };
  const up = (e) => {
    if (!start || !dragging) { start = null; return; }
    const p = last.prev || start, v = (e.clientX - p.x) / Math.max(1, e.timeStamp - p.t);
    const out = swipeOutcome(dx, v, el.offsetWidth);
    const from = { translate: dx + 'px 0', opacity: el.style.opacity || '1' };
    el.style.translate = ''; el.style.opacity = '';
    start = null; dragging = false;
    if (out.dismiss) {
      const a = play(el, [from, { translate: out.dir * (el.offsetWidth + 40) + 'px 0', opacity: 0 }], { spring: 'effects-fast', fill: 'forwards' });
      if (a) a.finished.then(onDismiss, () => {}); else onDismiss();
    } else play(el, [from, { translate: '0px 0', opacity: 1 }], { spring: 'spatial-fast' });
  };
  el.addEventListener('pointerdown', down); el.addEventListener('pointermove', move);
  el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
  return () => { el.removeEventListener('pointerdown', down); el.removeEventListener('pointermove', move); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', up); };
}
